// Number of logo/flyer artboards available locally
const LOGO_ARTBOARD_COUNT = 20;
const FLYER_ARTBOARD_COUNT = 20;
const MOBILE_APP_ARTBOARD_COUNT = 4; // Based on the files available in public/MobileApps/1x/
const UIUX_ARTBOARD_COUNT = 0; // Currently empty

// Interface for design assets
export interface DesignAsset {
  id: number;
  title: string;
  description: string;
  url: string;
}

/**
 * Checks if a file exists by fetching its headers
 */
const doesFileExist = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (e) {
    return false;
  }
};

/**
 * Generates an array of design assets from the public folder
 * @param type 'Logos', 'Flyers', 'MobileApps', or 'UIUX'
 * @returns Array of design assets
 */
export const getLocalDesigns = (type: 'Logos' | 'Flyers' | 'MobileApps' | 'UIUX'): DesignAsset[] => {
  const designs: DesignAsset[] = [];
  
  if (type === 'Logos' || type === 'Flyers') {
    // The files start from Artboard 2.jpg, not Artboard 1.jpg
    const artboardNumbers = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
    
    // For each Artboard file
    artboardNumbers.forEach((num, index) => {
      // Use absolute path for asset URL
      const url = `/${type}/Artboard ${num}.jpg`;
      
      // Add to designs array
      designs.push({
        id: index + 1,
        title: `${type === 'Logos' ? 'Logo' : 'Flyer'} Design ${num}`,
        description: `${type === 'Logos' ? 'Logo' : 'Flyer'} design concept`,
        url
      });
    });
  } else if (type === 'MobileApps') {
    // Mobile App assets
    for (let i = 1; i <= MOBILE_APP_ARTBOARD_COUNT; i++) {
      const url = `/MobileApps/1x/Artboard ${i}.png`;
      designs.push({
        id: i,
        title: `Mobile App Design ${i}`,
        description: `Mobile application design concept`,
        url
      });
    }
  } else if (type === 'UIUX') {
    // Currently no UIUX assets in the public folder
    // This is a placeholder for when assets are added
    for (let i = 1; i <= UIUX_ARTBOARD_COUNT; i++) {
      const url = `/UIUX/Artboard ${i}.png`;
      designs.push({
        id: i,
        title: `UI/UX Design ${i}`,
        description: `User interface design concept`,
        url
      });
    }
  }
  
  console.log(`Generated ${designs.length} ${type} designs`);
  return designs;
};

/**
 * Get logo designs from the local public folder
 */
export const getLogoDesigns = (): DesignAsset[] => {
  return getLocalDesigns('Logos');
};

/**
 * Get flyer designs from the local public folder
 */
export const getFlyerDesigns = (): DesignAsset[] => {
  return getLocalDesigns('Flyers');
};

/**
 * Get mobile app designs from the local public folder
 */
export const getMobileAppDesigns = (): DesignAsset[] => {
  return getLocalDesigns('MobileApps');
};

/**
 * Get UI/UX designs from the local public folder
 */
export const getUIUXDesigns = (): DesignAsset[] => {
  return getLocalDesigns('UIUX');
}; 